const defaultNormalize = (value) => value.replace(/\D/g, '');
// Finds the caret index in `formattedValue` that sits immediately after the
// `digitCount`-th digit, so the caret can be restored to "the same spot
// relative to the digits" after the display text is reformatted around it.
const getCaretPositionAfterDigitCount = (formattedValue, digitCount) => {
    if (digitCount <= 0)
        return 0;
    let digitsSeen = 0;
    for (let index = 0; index < formattedValue.length; index += 1) {
        if (/\d/.test(formattedValue[index])) {
            digitsSeen += 1;
            if (digitsSeen === digitCount)
                return index + 1;
        }
    }
    return formattedValue.length;
};
/**
 * Pure computation for a single masked-input edit: takes the raw (already
 * mask-decorated) input text plus the caret position and previously shown
 * text, and returns the normalized digits, the reformatted display text,
 * and where the caret should land afterwards.
 *
 * This has no dependency on React, refs, or DOM APIs, so it's plain data in
 * and data out — it doesn't need to be a hook, since there's no render-cycle
 * state to manage and no reuse across multiple components; it's just a
 * pure function extracted for readability and direct unit testing.
 */
const computeMaskedInputChange = ({ mask, rawValue, caretPosition, previousFormattedValue, }) => {
    const normalize = mask?.normalize ?? defaultNormalize;
    const format = mask?.format ?? ((digits) => digits);
    let digits = normalize(rawValue);
    let digitsBeforeCaret = defaultNormalize(rawValue.slice(0, caretPosition)).length;
    // A single backspace/delete keystroke can land on a mask-inserted
    // character (e.g. the area code's closing parenthesis) instead of a
    // digit. When that happens the digit string comes out unchanged even
    // though the visible text got shorter, so simply reformatting would just
    // reinstate the exact same character — making backspace look like it
    // silently did nothing. Detect that single-character removal and drop
    // the adjacent digit too, so one keystroke always removes exactly one
    // digit.
    const removedExactlyOneCharacter = previousFormattedValue.length - rawValue.length === 1;
    if (removedExactlyOneCharacter && digits.length > 0) {
        const previousDigits = normalize(previousFormattedValue);
        if (digits === previousDigits) {
            const removeIndex = Math.max(digitsBeforeCaret - 1, 0);
            digits = digits.slice(0, removeIndex) + digits.slice(removeIndex + 1);
            digitsBeforeCaret = removeIndex;
        }
    }
    const formattedValue = format(digits);
    return {
        digits,
        formattedValue,
        caretPosition: getCaretPositionAfterDigitCount(formattedValue, digitsBeforeCaret),
    };
};

export { computeMaskedInputChange, defaultNormalize };
